/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Footer Component
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.0.0
 */

import { Fragment, useCallback } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import ICommonTranslation from '../../../modules/internationalization/types/common';
import { ELogBusinessEventName, ELogBusinessEventType } from '../../../modules/logging/loggingConstants';
import { makeSelectOnboardingIsActive } from '../../../modules/onboarding/onboardingSelectors';
import { AccessComponentKeyEnum } from '../../../modules/role/constants/guard/guardConstants';
import Guard from '../../../modules/role/Guard';
import { guardComponentType } from '../../../modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '../../../modules/role/selectors/guardSelector';
import {
    makeSelectNewsServiceAccessLoggedIn,
    makeSelectUsername,
    makeSelectUserTypeId,
} from '../../../pages/PageAuth/selectors/authSelectors';
import { getCampaignsPageUrl } from '../../../pages/PageCampaign/utils/getCampaignsPageUrl';
import { IRootReducer } from '../../../reducers/rootReducer';
import { RoutePath } from '../../../routes/constants/routesConstants';
import { EllipseText } from '../index';
import FooterLink from './FooterLink';
import { CopyrightStyled, FooterBodyStyled, FooterContainerStyled, FooterLinkGroupStyled } from './styled/FooterStyled';

const replacerMomentFormat = (value: string) => {
    return moment().format(value.replace(/{|}/g, ''));
};

export const resolveCopyright = (str: string) => {
    const regex = /(\{[^{|}]+\})/g;

    return str.replace(regex, replacerMomentFormat);
};

const Footer = ({
    brandText,
    isOnboardingActive,
    isNewsServiceUser,
    tabMenuCompactView,
    username,
    usernamePrefix,
    userTypeId,
    userTypes,
}: ICommonTranslation & IConnectedProps) => {
    const getUserTypeName = useCallback(() => userTypes[userTypeId] ?? userTypeId.toString(), [userTypes, userTypeId]);
    const userInfo = `${isNewsServiceUser ? '' : usernamePrefix} ${username}`;

    return (
        <FooterContainerStyled style={{ zIndex: isOnboardingActive ? 1 : 500 }}>
            <FooterBodyStyled>
                <FooterLinkGroupStyled className="footer__links">
                    <Guard accessKey={AccessComponentKeyEnum.TabMenuMain}>
                        <FooterLink dataAttr="account" label={tabMenuCompactView.main} to={RoutePath.main} />
                    </Guard>
                    <Guard accessKey={AccessComponentKeyEnum.FooterAccount}>
                        <FooterLink
                            dataAttr="account"
                            label={tabMenuCompactView.account}
                            logBusinessEventPayload={{
                                eventName: ELogBusinessEventName.USER_ACCOUNTS,
                                type: ELogBusinessEventType.common,
                            }}
                            to={RoutePath.account}
                        />
                    </Guard>
                    <Guard accessKey={AccessComponentKeyEnum.FooterServicesSubscriberMobile}>
                        <FooterLink
                            dataAttr="subscriber_mobile"
                            label={tabMenuCompactView.subscribersMobile}
                            logBusinessEventPayload={{
                                eventName: ELogBusinessEventName.USER_MOBILE_SUBSCRIBERS,
                                type: ELogBusinessEventType.common,
                            }}
                            to={RoutePath.subscriberMobile}
                        />
                    </Guard>
                    <Guard accessKey={AccessComponentKeyEnum.FooterProducts}>
                        <FooterLink
                            dataAttr="products"
                            label={tabMenuCompactView.products}
                            logBusinessEventPayload={{
                                eventName: ELogBusinessEventName.USER_PAGE_PRODUCT_BROWSE,
                                type: ELogBusinessEventType.common,
                            }}
                            to={RoutePath.products}
                        />
                    </Guard>
                    <Guard accessKey={AccessComponentKeyEnum.FooterHistoryOrder}>
                        <FooterLink
                            dataAttr="orders"
                            label={tabMenuCompactView.orders}
                            logBusinessEventPayload={{
                                eventName: ELogBusinessEventName.USER_ORDER_HISTORY,
                                type: ELogBusinessEventType.common,
                            }}
                            to={RoutePath.orders}
                        />
                    </Guard>
                    <Guard accessKey={AccessComponentKeyEnum.FooterHistoryInquiry}>
                        <FooterLink
                            dataAttr="inquiries"
                            label={tabMenuCompactView.inquiries}
                            logBusinessEventPayload={{
                                eventName: ELogBusinessEventName.USER_INQUIRIES,
                                type: ELogBusinessEventType.common,
                            }}
                            to={RoutePath.inquiries}
                        />
                    </Guard>
                    <Guard accessKey={AccessComponentKeyEnum.FooterOffers}>
                        <FooterLink
                            dataAttr="offers"
                            label={tabMenuCompactView.offers}
                            to={getCampaignsPageUrl()}
                        />
                    </Guard>
                </FooterLinkGroupStyled>
                <CopyrightStyled>
                    <Fragment>
                        <EllipseText title={userInfo} tooltipMsg={getUserTypeName()}>
                            {userInfo} ({getUserTypeName()}
                        </EllipseText>
                        &nbsp;)&nbsp;-&nbsp;
                    </Fragment>
                    {resolveCopyright(brandText.copyright)}
                </CopyrightStyled>
            </FooterBodyStyled>
        </FooterContainerStyled>
    );
};

export interface IConnectedProps {
    guardComponents: guardComponentType;
    isOnboardingActive: boolean;
    isNewsServiceUser: boolean;
    username: string;
    userTypeId: number;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    guardComponents: makeSelectGuardComponents(),
    isOnboardingActive: makeSelectOnboardingIsActive(),
    isNewsServiceUser: makeSelectNewsServiceAccessLoggedIn(),
    username: makeSelectUsername(),
    userTypeId: makeSelectUserTypeId(),
});

export default connect(mapStateToProps)(Footer);
